import { Dialog, DialogContent, DialogFooter } from '@/components/ui/Dialog';
import { useAuth } from '@/context/AuthContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { formatTimeAgo } from '@unriddle/api/slack';
import dayjs from 'dayjs';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './Button/Button';

interface NPSScaleProps {
  onSelect: (number: number | null) => void;
  selectedNumber: number | null;
}

const NPSScale: React.FC<NPSScaleProps> = ({ onSelect, selectedNumber }) => {
  const scale = Array.from({ length: 10 }, (_, i) => i + 1);

  const handleSelect = (number: number) => {
    const newSelectedNumber = selectedNumber === number ? null : number;
    onSelect(newSelectedNumber);
  };

  return (
    <div className="flex flex-col">
      <div className="flex w-full border justify-center rounded-md border-border">
        {scale.map((number) => (
          <button
            key={number}
            className={`w-7 md:w-11 h-8 flex items-center justify-center focus:outline-none border-border ${
              number !== 1 ? 'border-l' : ''
            } ${selectedNumber === number ? 'bg-popover-foreground text-background' : ''} ${
              number === 1
                ? 'rounded-l-md'
                : number === 10
                  ? 'rounded-r-md'
                  : ''
            }`}
            onClick={() => handleSelect(number)}
          >
            {number}
          </button>
        ))}
      </div>
      <div className="flex justify-between w-full mt-2 text-sm text-textGray2">
        <span>Very unlikely</span>
        <span>Very likely</span>
      </div>
    </div>
  );
};

export const showNpsModalAtom = atom(false);
export const visitCountAtom = atomWithStorage('visitCount', 1);
export const useShowNpsModal = () => useAtom(showNpsModalAtom);

const NPSModal: React.FC = () => {
  const [showNPSModal, setShowNPSModal] = useShowNpsModal();
  const [visitCount, setVisitCount] = useAtom(visitCountAtom);
  const { user } = useAuth();
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { track } = useAnalytics();
  const { mutateAsync: sendMessageToSlack } = api.slack.message.useMutation();
  const { mutateAsync: createNPSResponse } =
    api.user.createNPSResponse.useMutation();

  const onSubmit = async () => {
    if (!selectedNumber) return;

    setIsSubmitting(true);
    setShowNPSModal(false);
    toast.success('Feedback submitted!');

    track('NPS Submit', {
      score: selectedNumber,
      feedback,
      method: 'Submit',
    });

    const answers = user?.onboardingResponse?.[0];

    const messageParts = [
      'New NPS submission:',
      `- From: ${user ? user.email : 'visitor'}`,
      `- Score: ${selectedNumber}/10`,
      feedback ? `- Feedback: ${feedback}` : null,
      answers?.hearAboutUs ? `- Source: ${answers?.hearAboutUs}` : null,
      answers?.purpose ? `- Purpose: ${answers?.purpose}` : null,
      answers?.educationLevel
        ? `- Education Level: ${answers?.educationLevel}`
        : null,
      user?.createdAt
        ? `- Account created: ${formatTimeAgo(new Date(user?.createdAt))}`
        : null,
    ].filter(Boolean);

    const slackMessage = messageParts.join('\n');

    try {
      await createNPSResponse({
        score: selectedNumber,
        feedback,
      });

      await sendMessageToSlack({
        type: 'nps',
        message: slackMessage,
      });
    } catch (error) {
      console.error('Error submitting NPS feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    setShowNPSModal(false);

    track('NPS Submit', {
      score: selectedNumber,
      feedback,
      method: 'Close',
    });

    if (selectedNumber !== null) {
      const answers = user?.onboardingResponse?.[0];
      const messageParts = [
        'New NPS submission:',
        `- From: ${user ? user.email : 'visitor'}`,
        `- Score: ${selectedNumber}/10`,
        feedback ? `- Feedback: ${feedback}` : null,
        answers?.hearAboutUs ? `- Source: ${answers?.hearAboutUs}` : null,
        answers?.purpose ? `- Purpose: ${answers?.purpose}` : null,
        answers?.educationLevel
          ? `- Education Level: ${answers?.educationLevel}`
          : null,
        user?.createdAt
          ? `- Account created: ${formatTimeAgo(new Date(user?.createdAt))}`
          : null,
      ].filter(Boolean);

      const slackMessage = messageParts.join('\n');

      try {
        await createNPSResponse({
          score: selectedNumber,
          feedback,
        });

        await sendMessageToSlack({
          type: 'nps',
          message: slackMessage,
        });
      } catch (error) {
        console.error('Error submitting NPS feedback:', error);
      }
    } else {
      localStorage.setItem('npsModalClosed', 'true');
    }
  };

  useEffect(() => {
    if (
      user &&
      !user.npsResponse?.length &&
      !localStorage.getItem('npsModalClosed')
    ) {
      console.log('no NPS response found');
      const userCreatedAt = dayjs(user.createdAt);
      const daysSinceCreation = dayjs().diff(userCreatedAt, 'day');

      if (daysSinceCreation > 3 && Math.random() < 0.2 && visitCount >= 4) {
        setTimeout(() => {
          setShowNPSModal(true);
        }, 8000);
      }
      setVisitCount((prev) => prev + 1);
    }
  }, [user]);

  return (
    <Dialog
      modal={false}
      open={showNPSModal}
      onOpenChange={() => {
        handleClose();
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        hideOverlay
        side="bottom"
        className="w-fit"
      >
        <div className="flex flex-col gap-4">
          <span className=" text-sm md:text-base font-semibold pr-6 text-foreground">
            How likely are you to recommend us to a friend?
          </span>
          <NPSScale
            onSelect={setSelectedNumber}
            selectedNumber={selectedNumber}
          />
        </div>

        {selectedNumber !== null && (
          <div className="flex flex-col gap-4">
            <span className="text-sm font-medium text-foreground">
              We read every single response. Why did you rate Anara a{' '}
              {selectedNumber}?
            </span>

            <textarea
              rows={4}
              value={feedback}
              name="feedback"
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="We'd love to know!"
              className="w-full p-2 text-sm mb-2 h-full focus:outline-none placeholder:text-[#999999] resize-none bg-popover rounded-lg border border-border"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
        )}

        {selectedNumber !== null && (
          <DialogFooter>
            <Button
              size="sm"
              onClick={onSubmit}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NPSModal;
